import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import {
  systemUpdatesApi,
  SystemUpdate,
  ReleaseWithSections,
} from '../api/systemUpdates';

type Persona =
  | 'everyone'
  | 'system_admin'
  | 'partner_admin'
  | 'company_admin'
  | 'compliance_user'
  | 'internal_auditor'
  | 'external_auditor';

const PERSONA_LABELS: Record<Persona, string> = {
  everyone: 'Everyone',
  system_admin: 'System Admin',
  partner_admin: 'Partner Admin',
  company_admin: 'Company Admin',
  compliance_user: 'Compliance User',
  internal_auditor: 'Internal Auditor',
  external_auditor: 'External Auditor',
};

/**
 * Derive the viewer's persona slug from their loaded permission set so we
 * can show them only the section of each release relevant to their role.
 * Uses the same permission families the backend persona resolver uses.
 */
function derivePersona(permissions: string[]): Persona {
  const has = (p: string) => permissions.includes(p);
  if (has('system.admin') || has('superadmin') || has('system.config')) return 'system_admin';
  if (has('partner.dashboard.view') || has('partner.companies.view')) return 'partner_admin';
  if (has('audit.certification') || has('audit.external.reports.view')) return 'external_auditor';
  if (has('audit.findings.manage') || has('audit.internal.reports.view')) return 'internal_auditor';
  if (has('company.admin') || has('users.manage') || has('company.dashboard.view')) return 'company_admin';
  if (has('compliance.dashboard.view') || has('compliance.evidence.upload')) return 'compliance_user';
  return 'everyone';
}

const CATEGORY_COLORS: Record<string, string> = {
  feature: 'bg-blue-50 text-blue-700 border-blue-200',
  bugfix: 'bg-amber-50 text-amber-700 border-amber-200',
  security: 'bg-red-50 text-red-700 border-red-200',
  performance: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  maintenance: 'bg-slate-50 text-slate-700 border-slate-200',
  announcement: 'bg-purple-50 text-purple-700 border-purple-200',
  ai_capability: 'bg-indigo-50 text-indigo-700 border-indigo-200',
};

export default function ChangelogPage() {
  const { user, hasPermission } = useAuth();
  const [searchParams] = useSearchParams();
  const highlightedId = searchParams.get('v') ? Number(searchParams.get('v')) : null;

  const [updates, setUpdates] = useState<SystemUpdate[]>([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState<Record<number, ReleaseWithSections | null>>({});
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editDraft, setEditDraft] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);

  const persona = useMemo(
    () => derivePersona((user?.permissions as string[]) || []),
    [user],
  );
  const isSystemAdmin = persona === 'system_admin';

  useEffect(() => {
    (async () => {
      setLoading(true);
      const res = await systemUpdatesApi.listReleases(50, 0);
      setUpdates(res.updates || []);
      setLoading(false);
      if (highlightedId) {
        await loadFull(highlightedId);
        requestAnimationFrame(() => {
          const el = document.getElementById(`release-${highlightedId}`);
          if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        });
      }
    })();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const loadFull = async (id: number) => {
    if (expanded[id]) return;
    const full = await systemUpdatesApi.getRelease(id);
    setExpanded(prev => ({ ...prev, [id]: full }));
  };

  const startEdit = (rel: ReleaseWithSections) => {
    const sections = rel.sections || {};
    setEditingId(rel.id);
    setEditDraft({
      title: rel.title,
      ...sections,
    });
  };

  const saveEdit = async (id: number) => {
    setSaving(true);
    const { title, ...sections } = editDraft;
    const ok = await systemUpdatesApi.updateRelease(id, {
      title,
      sections: sections as Record<string, string>,
      markReviewed: true,
    });
    setSaving(false);
    if (ok) {
      setEditingId(null);
      const fresh = await systemUpdatesApi.getRelease(id);
      setExpanded(prev => ({ ...prev, [id]: fresh }));
      setUpdates(prev => prev.map(u => u.id === id ? { ...u, title: title || u.title } : u));
    }
  };

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-slate-900">What's new</h1>
        <p className="mt-2 text-slate-600">
          Changes to EIS-Tech, written for your role ({PERSONA_LABELS[persona]}).
        </p>
      </div>

      {loading && <div className="text-slate-500">Loading release notes…</div>}

      {!loading && updates.length === 0 && (
        <div className="rounded-lg border border-slate-200 bg-white p-8 text-center text-slate-500">
          No release notes yet. Check back after the next deployment.
        </div>
      )}

      <div className="space-y-6">
        {updates.map(u => {
          const full = expanded[u.id];
          const isHighlighted = highlightedId === u.id;
          const categoryClass = CATEGORY_COLORS[u.category] || CATEGORY_COLORS.feature;
          return (
            <div
              key={u.id}
              id={`release-${u.id}`}
              className={`rounded-lg border bg-white shadow-sm transition ${isHighlighted ? 'border-blue-400 ring-2 ring-blue-100' : 'border-slate-200'}`}
            >
              <div className="p-6">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <span className={`text-xs font-semibold uppercase tracking-wide px-2 py-1 rounded border ${categoryClass}`}>
                        {u.category.replace('_', ' ')}
                      </span>
                      {u.version && (
                        <span className="text-xs text-slate-500 font-mono">v{u.version}</span>
                      )}
                      <span className="text-xs text-slate-400">
                        {new Date(u.created_at).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' })}
                      </span>
                    </div>
                    <h2 className="text-xl font-semibold text-slate-900">{u.title}</h2>
                  </div>
                  <button
                    onClick={() => loadFull(u.id)}
                    className="text-sm text-blue-600 hover:text-blue-700 font-medium whitespace-nowrap"
                  >
                    {full ? 'Collapse' : 'Read more →'}
                  </button>
                </div>

                {!full && (
                  <p className="mt-3 text-slate-600 text-sm line-clamp-2">{u.summary}</p>
                )}

                {full && (
                  <div className="mt-4 space-y-4">
                    {full.ai_status === 'needs_review' && (
                      <div className="rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
                        <strong>Pending admin review.</strong> These notes were generated automatically from the latest deployment. A system administrator will refine them shortly; the information below is accurate but may be rephrased.
                      </div>
                    )}

                    {editingId === u.id && isSystemAdmin ? (
                      <div className="space-y-3">
                        <label className="block">
                          <span className="text-xs font-semibold text-slate-600 uppercase tracking-wide">Title</span>
                          <input
                            className="mt-1 w-full rounded border border-slate-300 px-3 py-2 text-sm"
                            value={editDraft.title || ''}
                            onChange={e => setEditDraft(p => ({ ...p, title: e.target.value }))}
                          />
                        </label>
                        {(['everyone', 'company_admin', 'compliance_user', 'internal_auditor', 'external_auditor', 'partner_admin', 'system_admin'] as Persona[]).map(p => (
                          <label key={p} className="block">
                            <span className="text-xs font-semibold text-slate-600 uppercase tracking-wide">{PERSONA_LABELS[p]}</span>
                            <textarea
                              className="mt-1 w-full rounded border border-slate-300 px-3 py-2 text-sm font-mono"
                              rows={4}
                              value={editDraft[p] || ''}
                              onChange={e => setEditDraft(prev => ({ ...prev, [p]: e.target.value }))}
                            />
                          </label>
                        ))}
                        <div className="flex gap-2 pt-2">
                          <button
                            onClick={() => saveEdit(u.id)}
                            disabled={saving}
                            className="rounded bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
                          >
                            {saving ? 'Saving…' : 'Save & mark reviewed'}
                          </button>
                          <button
                            onClick={() => { setEditingId(null); setEditDraft({}); }}
                            className="rounded border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    ) : (
                      <>
                        {full.sections && full.sections[persona] && (
                          <section>
                            <h3 className="text-sm font-semibold text-slate-700 mb-2">For {PERSONA_LABELS[persona]}</h3>
                            <div className="prose prose-sm max-w-none text-slate-700 whitespace-pre-wrap">
                              {full.sections[persona]}
                            </div>
                          </section>
                        )}

                        {full.sections && persona !== 'everyone' && full.sections.everyone && (
                          <section className="border-t pt-4">
                            <h3 className="text-sm font-semibold text-slate-500 mb-2">Overview</h3>
                            <div className="text-sm text-slate-600 whitespace-pre-wrap">{full.sections.everyone}</div>
                          </section>
                        )}

                        {!full.sections && (
                          <div className="text-sm text-slate-700 whitespace-pre-wrap">{full.details || full.summary}</div>
                        )}

                        {isSystemAdmin && (
                          <div className="pt-3 border-t">
                            <button
                              onClick={() => startEdit(full)}
                              className="text-sm text-blue-600 hover:text-blue-700 font-medium"
                            >
                              Edit release notes
                            </button>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
